import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Navigate, useLocation, useNavigate } from 'react-router';
import { toast } from 'sonner';
import { configuration, connexionLocale, connexionMicrosoft, retourMicrosoftEnCours, terminerRetourMicrosoft, useSession } from '@/auth/session';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';

interface Formulaire {
  email: string;
  motDePasse: string;
  codeTotp: string;
}

export function Connexion() {
  const session = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const cfg = useQuery({ queryKey: ['configuration'], queryFn: configuration, staleTime: Infinity });
  const [enCours, setEnCours] = useState<'microsoft' | 'local' | null>(retourMicrosoftEnCours() ? 'microsoft' : null);
  const [codeRequis, setCodeRequis] = useState(false);
  const retourTraite = useRef(false);
  const form = useForm<Formulaire>({ defaultValues: { email: '', motDePasse: '', codeTotp: '' } });
  const destination = (location.state as { de?: string } | null)?.de ?? '/tableau-de-bord';

  // Retour de Microsoft (redirection pleine page) : la réponse est dans l'adresse, on l'échange contre une session.
  useEffect(() => {
    if (!cfg.data || retourTraite.current || !retourMicrosoftEnCours()) return;
    retourTraite.current = true;
    terminerRetourMicrosoft(cfg.data)
      .then((r) => {
        if (r) navigate(destination, { replace: true });
        else setEnCours(null);
      })
      .catch((e: Error) => {
        setEnCours(null);
        toast.error(e.message || 'Connexion Microsoft 365 impossible.');
      });
  }, [cfg.data, destination, navigate]);

  if (session) return <Navigate to={destination} replace />;

  const microsoft = async () => {
    if (!cfg.data) return;
    setEnCours('microsoft');
    try {
      await connexionMicrosoft(cfg.data);
    } catch (e) {
      setEnCours(null);
      toast.error(e instanceof Error ? e.message : 'Connexion Microsoft 365 impossible.');
    }
  };

  const local = form.handleSubmit(async (v) => {
    setEnCours('local');
    try {
      const r = await connexionLocale(v.email.trim(), v.motDePasse, v.codeTotp.trim() || undefined);
      // Mot de passe initial remis par l'administrateur : à remplacer avant de continuer.
      if (r.doitChangerMotDePasse) navigate('/mot-de-passe', { replace: true, state: { obligatoire: true } });
      else navigate(destination, { replace: true });
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Connexion impossible.';
      if (message.includes('Code à usage unique requis')) setCodeRequis(true);
      toast.error(message);
    } finally {
      setEnCours(null);
    }
  });

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-primary/10 via-background to-background px-4 py-10">
      <div className="w-full max-w-md space-y-6">
        <div className="flex flex-col items-center gap-3 text-center">
          <img src="/logo.png" alt="FONAREV" className="size-20 object-contain" />
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">FONAREV 360</h1>
            <p className="text-sm text-muted-foreground">Plateforme de gestion intégrée du Fonds national des réparations des victimes</p>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Connexion</CardTitle>
            <CardDescription>Avec votre compte Microsoft 365 du FONAREV, ou un compte local.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <Button className="w-full" size="lg" onClick={microsoft} disabled={!cfg.data?.microsoft || enCours !== null}>
              <svg viewBox="0 0 21 21" className="size-4" aria-hidden>
                <rect x="1" y="1" width="9" height="9" fill="#f25022" />
                <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
                <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
                <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
              </svg>
              {enCours === 'microsoft' ? 'Connexion Microsoft 365 en cours…' : 'Se connecter avec Microsoft 365'}
            </Button>
            {cfg.data && !cfg.data.microsoft && <p className="text-xs text-muted-foreground">La connexion Microsoft 365 n'est pas configurée sur ce serveur.</p>}

            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <Separator className="flex-1" />
              compte local
              <Separator className="flex-1" />
            </div>
            {cfg.data?.connexionLocaleRestreinte && (
              <p className="text-xs text-muted-foreground">Connexion locale réservée aux administrateurs ; les autres agents se connectent avec Microsoft 365. En cas d'indisponibilité de Microsoft 365, l'administrateur active le mode de secours.</p>
            )}

            <form onSubmit={local} className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="email">Adresse</Label>
                <Input id="email" type="email" autoComplete="username" placeholder="prenom.nom@test.fonarev360.test" {...form.register('email', { required: true })} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="mdp">Mot de passe</Label>
                <Input id="mdp" type="password" autoComplete="current-password" {...form.register('motDePasse', { required: true })} />
              </div>
              {codeRequis && (
                <div className="space-y-1.5">
                  <Label htmlFor="totp">Code de l'application d'authentification</Label>
                  <Input id="totp" inputMode="numeric" maxLength={7} placeholder="123456" {...form.register('codeTotp')} />
                </div>
              )}
              <Button type="submit" variant="secondary" className="w-full" disabled={enCours !== null}>
                {enCours === 'local' ? 'Connexion en cours…' : 'Se connecter'}
              </Button>
            </form>
          </CardContent>
        </Card>
        <p className="text-center text-xs text-muted-foreground">Version de test · données fictives · chaque action est inscrite au journal d'audit.</p>
      </div>
    </div>
  );
}
